import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { Form, Button, Input, Popup } from 'semantic-ui-react';
import { FlexCard } from './../';
import styled from 'styled-components';
import { useTableSearch } from './useTableSearch';

export const TableSearch = observer(
  ({
    fields,
    onSearch,
    onClear,
    initialSearchParams,
    isLoading,
    debugging,
    children,
  }) => {
    const {
      getDefaultValue,
      clearSearchValues,
      handleSearch,
      isSearching,
      onSearchValueChanged,
      onSearchValueKeyUp,
      searchValuesEmpty,
    } = useTableSearch({ onSearch, onClear, initialSearchParams, debugging });

    return (
      <FlexCard horizontalAlignment="center">
        <Form data-testid="search-form">
          <Form.Field inline>
            <SearchFieldWrapper
              verticalAlignment="center"
              horizontalAlignment="center"
              data-testid="search-wrapper"
            >
              <FlexCard
                style={{ padding: '5px 0px 0px 0px' }}
                horizontalAlignment="center"
                data-testid="search-filters"
              >
                {children}
              </FlexCard>
              {fields.map(({ label, param, inputWidth }) => (
                <SearchInput
                  data-testid="search-input"
                  defaultValue={getDefaultValue(param)}
                  inputWidth={inputWidth}
                  key={`search_${label}`}
                  label={label}
                  onChange={onSearchValueChanged}
                  onKeyUp={onSearchValueKeyUp}
                  param={param}
                />
              ))}
              <Popup
                data-testid="search-popup"
                content="Once you've entered your search criteria, you may click this button or press the Enter key on your keyboard to search for that information."
                header="Table Search"
                position="bottom center"
                size="tiny"
                trigger={
                  <Button
                    data-testid="search-button"
                    content="Search"
                    disabled={searchValuesEmpty}
                    icon="search"
                    loading={isLoading && isSearching}
                    onClick={handleSearch}
                    primary
                  />
                }
              />
              <Popup
                data-testid="clear-popup"
                content="Clears all search fields and resets your view as if you first entered the page. You may press the Escape key on your keyboard to also perform this operation."
                header="Reset Filters"
                position="bottom center"
                size="tiny"
                trigger={
                  <Button
                    data-testid="clear-button"
                    content="Reset"
                    icon="times circle outline"
                    onClick={clearSearchValues}
                    secondary
                  />
                }
              />
            </SearchFieldWrapper>
          </Form.Field>
        </Form>
      </FlexCard>
    );
  },
);

TableSearch.propTypes = {
  onSearch: PropTypes.func.isRequired,
  fields: PropTypes.array.isRequired,
  debugging: PropTypes.bool,
};

TableSearch.defaultProps = {
  debugging: false,
};

export default TableSearch;

export const SearchInput = ({
  inputWidth,
  label,
  param,
  onChange,
  onKeyUp,
  defaultValue,
}) => {
  const style = inputWidth ? { width: inputWidth } : null;
  return (
    <Wrapper data-testid="search-field-wrapper" inputWidth={inputWidth}>
      <Input
        defaultValue={defaultValue}
        label={label}
        onChange={(e) => onChange(param, e.target.value)}
        onKeyUp={onKeyUp}
        placeholder={label}
        style={style}
        type="search"
      />
    </Wrapper>
  );
};

SearchInput.propTypes = {
  inputWidth: PropTypes.string,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyUp: PropTypes.func.isRequired,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

SearchInput.defaultProps = {
  defaultValue: null,
};

export const Wrapper = styled.div`
  /* background-color: cyan; */
  padding: 5px;
  & label {
    margin-right: 10px;
  }
`;

export const SearchFieldWrapper = styled(FlexCard)`
  margin: 0;
  padding: 10px;
  background-color: #fff;
  border: 1px solid #c7c7c7;
  border-radius: 10px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
`;
